import React, { Dispatch, SetStateAction, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import { Label } from "@components/common/label"
import { Input } from "@components/common/input"
import { Textarea } from "@components/common/textarea"
import { Button } from "@components/common/button"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import {
  FINANCIAL_SERVER_URL,
  PRICE_LIST_ALL_ENDPOINT,
  PRICE_LIST_BY_ID_ENDPOINT,
  REQUEST_ADD_NEW_BOQ_ITEM_ENDPOINT,
} from "@pma/utils/api-routes"
import { DictType } from "@pma/types"
import axios from "axios"
import { useMutation, useQuery } from "@tanstack/react-query"
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { ChevronsUpDown, PlusCircle } from "lucide-react"
import { useAuth } from "@pma/stores/auth-store"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { Separator } from "@components/common/separator"
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pma/components/common/table"
import { Checkbox } from "@components/common/checkbox"
import PmsLoader from "@components/common/loader"
import { formatCurrency } from "@pma/utils/utils"

interface Props {
  boqId: string
  showAddItemDialog: boolean
  setShowAddItemDialog: Dispatch<SetStateAction<boolean>>
  refetchBoQ: CallableFunction
}

const AddNewBoQItem = ({ showAddItemDialog, setShowAddItemDialog, boqId, refetchBoQ }: Props) => {
  const [fromPriceList, setFromPriceList] = useState(true)
  const [isLoadingAddMultiple, setIsLoading] = useState(false)
  const [activePriceList, setActivePriceList] = useState<undefined | number>(undefined)
  const [newItem, setNewItem] = useState({
    number: "",
    description: "",
    unit: "",
    amount: 0,
    cost: 0,
  })
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [itemAmounts, setItemAmounts] = useState<DictType>({})

  const { setTabManager, authConfig } = useAuth()

  const { translate, locale } = useTranslator()

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const orgId = authConfig?.last_organization_id

  const isHebrew = locale === "he"

  const getPriceLists = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + PRICE_LIST_ALL_ENDPOINT,
      {
        organization_id: orgId,
        show_deleted: false,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const { data: priceLists } = useQuery({
    queryKey: ["price-lists", orgId],
    queryFn: getPriceLists,
    enabled: showAddItemDialog,
  })

  const getCompanyPriceList = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + PRICE_LIST_BY_ID_ENDPOINT + "",
      {},
      {
        params: {
          price_id: activePriceList,
          organization_id: orgId,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const { data: priceList, isLoading: isLoadingPriceList } = useQuery({
    queryKey: ["price-list", activePriceList],
    queryFn: getCompanyPriceList,
    enabled: !!activePriceList,
  })

  const priceListItems = priceList?.price_items?.items

  const addNewItem = async (boqItem: DictType) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + REQUEST_ADD_NEW_BOQ_ITEM_ENDPOINT,
      {
        boq_id: boqId,
        item_number: boqItem.number,
        item_description: boqItem.description,
        item_cost: boqItem.cost,
        item_amount: boqItem.amount,
        item_unit: boqItem.unit,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleAddNewBoQItemMutation = useMutation({
    mutationKey: ["new-boq-item"],
    mutationFn: (boqItem: DictType) => addNewItem(boqItem),
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("boq item added successfully"),
        variant: "success",
      })
      setNewItem({
        number: "",
        description: "",
        unit: "",
        amount: 0,
        cost: 0,
      })
      refetchBoQ()
      setShowAddItemDialog(false)
      setTabManager({ group: "main-boq", main: "item-requests", sub: "approval" })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "add item", translate),
        variant: "destructive",
      })
    },
  })

  const handleItemToggle = (itemId: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    )
  }

  const confirmAddItem = () => {
    handleAddNewBoQItemMutation.mutate(newItem)
  }

  const confirmAddItemFromPriceList = () => {
    setIsLoading(true)
    const newItems = selectedItems
      .map((id) => priceListItems?.find((item: DictType) => item.id === id))
      .map((item) => ({
        boq_id: boqId,
        number: item.number,
        description: item.description,
        cost: item.price,
        amount: itemAmounts[item?.id] || 0,
        unit: item.units,
      }))

    Promise.all(newItems.map((item) => addNewItem(item)))
      .then(() => {
        setIsLoading(false)

        toast({
          title: translate("Success"),
          description: translate("items added successfully"),
          variant: "success",
        })
        setNewItem({
          number: "",
          description: "",
          unit: "",
          amount: 0,
          cost: 0,
        })
        setItemAmounts({})
        setSelectedItems([])
        refetchBoQ()
        setShowAddItemDialog(false)
      })
      .catch((error) => {
        const errorResponse = error?.response?.data?.message

        setIsLoading(false)

        toast({
          title: translate("Error"),
          description: getFinancialErrorMessage(error, "add item", translate),
          variant: "destructive",
        })
      })
  }

  const selectedPriceList = priceLists?.find((project: DictType) => project.id == activePriceList)

  return (
    <div>
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent
          className={cn(
            "h-full w-full",
            fromPriceList && activePriceList ? "max-h-[95vh] max-w-[95vw]" : "h-fit",
            isHebrew ? "rtl" : "ltr",
          )}
        >
          <DialogHeader>
            <DialogTitle className={cn(isHebrew ? "text-right" : "text-left")}>
              {translate("Add New BoQ Item")}
            </DialogTitle>
            <DialogDescription className={cn(isHebrew ? "text-right" : "text-left")}>
              {translate("Add a new item to the Bill of Quantities")}.
            </DialogDescription>
          </DialogHeader>
          {!fromPriceList && (
            <div className='py-4'>
              <div className='grid gap-4'>
                <div className='grid gap-2'>
                  <Label htmlFor='item-number'>{translate("Item Number")}</Label>
                  <Input
                    id='item-number'
                    placeholder={translate("Enter item number")}
                    value={newItem.number}
                    onChange={(e) => setNewItem({ ...newItem, number: e.target.value })}
                  />
                </div>

                <div className='grid gap-2'>
                  <Label htmlFor='item-description'>{translate("Description")}</Label>
                  <Textarea
                    id='item-description'
                    placeholder={translate("Enter item description")}
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                  <div className='grid gap-2'>
                    <Label htmlFor='item-unit'>{translate("Unit")}</Label>
                    <Input
                      id='item-unit'
                      placeholder='מ"ר'
                      value={newItem.unit}
                      onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    />
                  </div>
                  <div className='grid gap-2'>
                    <Label htmlFor='item-amount'>{translate("Amount")}</Label>
                    <Input
                      id='item-amount'
                      type='number'
                      placeholder={translate("Enter amount")}
                      value={newItem.amount || ""}
                      onChange={(e) =>
                        setNewItem({ ...newItem, amount: Number.parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div className='grid gap-2'>
                    <Label htmlFor='item-cost'>{translate("Cost")}</Label>
                    <Input
                      id='item-cost'
                      type='number'
                      placeholder={translate("Enter cost")}
                      value={newItem.cost || ""}
                      onChange={(e) =>
                        setNewItem({ ...newItem, cost: Number.parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {fromPriceList && (
            <>
              {/* {JSON.stringify(priceList)} */}
              <div className='flex flex-col gap-2'>
                <Label>
                  {translate("Select Price List")}
                  <span className='text-destructive'>*</span>
                </Label>
                <Popover>
                  <PopoverTrigger className='flex h-10 w-full items-center justify-between rounded-md border p-1.5'>
                    <div className='text-sm'>
                      {activePriceList ? (
                        <div className='w-[23ch] overflow-hidden truncate text-ellipsis p-0.5 text-left text-xs font-semibold capitalize'>
                          {selectedPriceList?.name}
                        </div>
                      ) : (
                        translate("Select Price List")
                      )}
                    </div>
                    <ChevronsUpDown size={12} />
                  </PopoverTrigger>

                  <PopoverContent className='p-0'>
                    <Command>
                      <CommandInput
                        className={cn(isHebrew ? "rtl" : "ltr")}
                        placeholder={translate("Search for price list") + "..."}
                      />
                      <CommandList>
                        <CommandEmpty>{translate("No price lists found")}.</CommandEmpty>
                        <CommandGroup>
                          {priceLists?.map((pricelist: DictType) => (
                            <PopoverClose className='w-full' key={pricelist?.id}>
                              <CommandItem
                                value={pricelist?.name}
                                defaultValue={pricelist?.name}
                                onSelect={() => setActivePriceList(pricelist?.id)}
                                className={cn(
                                  "my-1 capitalize",
                                  String(activePriceList) == String(pricelist.id) &&
                                    "bg-secondary/30 text-black",
                                )}
                              >
                                {pricelist?.name}
                              </CommandItem>
                            </PopoverClose>
                          ))}
                          <Separator />
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {activePriceList && (
                <div
                  id='estimate-items'
                  className='my-2 h-fit max-h-[200px] min-h-[60vh] w-full space-y-1 overflow-y-auto rounded-md border'
                >
                  <Table className='rounded-t-md'>
                    <TableHeader className='h-6 rounded-t-md bg-primary/20 p-0'>
                      <TableRow className='h-6 p-0 font-semibold'>
                        <TableHead className='h-full p-0.5'>
                          <div className='flex items-center space-x-2'>
                            <Checkbox
                              id={`select-all`}
                              checked={selectedItems?.length === priceListItems?.length}
                              onCheckedChange={() => {
                                if (selectedItems?.length === priceListItems?.length) {
                                  setSelectedItems([])
                                } else {
                                  setSelectedItems(
                                    priceListItems?.map((item: DictType) => item?.id),
                                  )
                                }
                              }}
                            />
                          </div>
                        </TableHead>
                        <TableHead className='h-full p-0.5'>{translate("Number")}</TableHead>
                        <TableHead className='h-full p-0.5'>{translate("Description")}</TableHead>
                        <TableHead className='h-full p-0.5'>{translate("Units")}</TableHead>
                        <TableHead className='h-full p-0.5'>{translate("Price")}</TableHead>
                        <TableHead className='h-full p-0.5'>{translate("Amount")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className={`space-y-2 overflow-y-scroll pt-2`}>
                      {priceListItems?.map((priceListItem: DictType) => (
                        <TableRow key={priceListItem?.id}>
                          <TableCell>
                            <div
                              key={priceListItem?.id}
                              className={cn(
                                "mb-0.5 flex items-start space-x-2",
                                isHebrew ? "rtl" : "ltr",
                              )}
                            >
                              <Checkbox
                                id={`item-${priceListItem.id}`}
                                checked={selectedItems?.includes(priceListItem?.id)}
                                onCheckedChange={() => handleItemToggle(priceListItem.id)}
                              />
                            </div>
                          </TableCell>
                          <TableCell className='font-medium'>{priceListItem?.number}</TableCell>

                          <TableCell className='text-wrap md:max-w-[160px]'>
                            {priceListItem?.description || "---"}
                          </TableCell>
                          <TableCell>{priceListItem?.units || ""}</TableCell>

                          <TableCell>
                            {priceListItem?.price
                              ? formatCurrency(priceListItem?.price, isHebrew)
                              : ""}
                          </TableCell>
                          <TableCell className='md:max-w-[160px]'>
                            {priceListItem?.price ? (
                              <Input
                                value={itemAmounts[priceListItem?.id] || 0}
                                onChange={(e) =>
                                  setItemAmounts((prev) => ({
                                    ...prev,
                                    [priceListItem?.id]: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              ""
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {isLoadingPriceList && (
                    <div className='my-2 flex h-[50px] w-full items-center justify-center rounded-md shadow-lg'>
                      <PmsLoader />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <DialogFooter className={cn(isHebrew ? "rtl" : "ltr")}>
            <div className='flex flex-1'>
              <Button variant={"link"} size={"sm"} onClick={() => setFromPriceList(!fromPriceList)}>
                {fromPriceList ? translate("Create new item") : translate("Add from price list")}?
              </Button>
            </div>
            <Button variant='outline' size={"sm"} onClick={() => setShowAddItemDialog(false)}>
              {translate("Cancel")}
            </Button>
            {fromPriceList ? (
              <Button
                onClick={confirmAddItemFromPriceList}
                size={"sm"}
                disabled={selectedItems.length < 1}
                loading={isLoadingAddMultiple}
              >
                <PlusCircle className='mr-1 h-4 w-4' />
                {translate("Add Item")}
              </Button>
            ) : (
              <Button
                onClick={confirmAddItem}
                size={"sm"}
                disabled={
                  !newItem.number || !newItem.description || !newItem.unit || !newItem.amount
                }
                loading={handleAddNewBoQItemMutation.isPending}
              >
                <PlusCircle className='mr-1 h-4 w-4' />
                {translate("Add Item")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AddNewBoQItem
